export interface CurrentLocationPincode {
  pincode: string;
  cityName: string;
  areaName: string;
}

export type CurrentLocationIssue = 'permission-denied' | 'position-unavailable' | 'position-timeout' |
  'secure-context-required' | 'lookup-unavailable' | 'lookup-denied' | 'lookup-timeout' | 'pincode-unavailable' | 'availability-unavailable';

export function currentLocationIssueMessage(issue?: CurrentLocationIssue | null): string {
  switch (issue) {
    case 'permission-denied': return 'Location access is blocked. Allow location in your browser site settings, then try again, or choose a saved address.';
    case 'position-unavailable': return 'Your device could not provide a location. Turn on device location services and try again, or choose a saved address.';
    case 'position-timeout': return 'Finding your location timed out. Try again, or choose a saved address.';
    case 'secure-context-required': return 'Current location requires a secure connection. Open the site using HTTPS, or choose a saved address.';
    case 'lookup-timeout': return 'Looking up your location timed out. Check your connection and try again, or choose a saved address.';
    case 'lookup-denied':
    case 'lookup-unavailable': return 'The location lookup service is unavailable right now. Try again, or choose a saved address.';
    case 'pincode-unavailable': return 'We could not find an Indian delivery pincode for this location. Choose a saved address or enter a delivery pincode.';
    case 'availability-unavailable': return 'Your location was found, but product availability could not be checked. Try again, or choose a saved address.';
    default: return 'Could not use your current location. You can try again or choose a saved address or delivery pincode.';
  }
}

const mapsClient = () => (window as Window & { google?: { maps?: typeof google.maps } }).google?.maps;

// Request one position, not continuous tracking. Coordinates stay in this call;
// only the resulting pincode is sent to Farm2Me's availability API.
export async function getCurrentLocationPincode(parentSignal?: AbortSignal,
  onUnavailable?: (issue: CurrentLocationIssue) => void): Promise<CurrentLocationPincode | null> {
  if (parentSignal?.aborted) return null;
  if (window.isSecureContext === false) { onUnavailable?.('secure-context-required'); return null; }
  if (!navigator.geolocation) { onUnavailable?.('position-unavailable'); return null; }
  if (!mapsClient()?.Geocoder && !process.env.REACT_APP_GOOGLE_MAPS_API_KEY) { onUnavailable?.('lookup-unavailable'); return null; }
  const controller = new AbortController();
  const stop = () => controller.abort();
  parentSignal?.addEventListener('abort', stop, { once: true });
  let issue: CurrentLocationIssue = 'position-unavailable';
  let succeeded = false;
  // Give device positioning and reverse geocoding independent time budgets.
  // A slow permission/GPS response must not consume the Maps loading budget.
  let deadline = window.setTimeout(() => { issue = 'position-timeout'; stop(); }, 20000);
  const { signal } = controller;
  try {
    const location = await new Promise<google.maps.LatLngLiteral | null>(resolve => {
      const finish = (value: google.maps.LatLngLiteral | null) => { signal.removeEventListener('abort', cancel); resolve(value); };
      const cancel = () => finish(null);
      signal.addEventListener('abort', cancel, { once: true });
      navigator.geolocation.getCurrentPosition(position => {
        if (signal.aborted) return finish(null);
        const { latitude: lat, longitude: lng } = position.coords;
        finish(Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null);
      }, error => {
        if (signal.aborted) return finish(null);
        issue = error.code === 1 ? 'permission-denied' : error.code === 3 ? 'position-timeout' : 'position-unavailable';
        finish(null);
      }, { enableHighAccuracy: false, maximumAge: 60000, timeout: 15000 });
    });
    if (!location || signal.aborted) return null;
    window.clearTimeout(deadline);
    issue = 'lookup-unavailable';
    deadline = window.setTimeout(() => {
      issue = 'lookup-timeout';
      if (!mapsClient()?.Geocoder) {
        const owned = document.querySelector<HTMLScriptElement>('script[data-farm2me-location-client="true"]');
        if (owned) owned.dataset.locationFailed = 'true';
      }
      stop();
    }, 15000);
    if (!await ensureGeocoder(signal)) return null;
    const result = await new Promise<CurrentLocationPincode | null>(resolve => {
      const finish = (value: CurrentLocationPincode | null) => { signal.removeEventListener('abort', cancel); resolve(value); };
      const cancel = () => finish(null);
      signal.addEventListener('abort', cancel, { once: true });
      new (mapsClient()!.Geocoder)().geocode({ location }, (results, status) => {
        if (signal.aborted) return finish(null);
        const found = status === 'OK' ? pincodeFromResults(results ?? []) : null;
        issue = status === 'REQUEST_DENIED' ? 'lookup-denied' : status === 'OK' || status === 'ZERO_RESULTS' ? 'pincode-unavailable' : 'lookup-unavailable';
        finish(found);
      });
    });
    succeeded = result !== null;
    return result;
  } catch {
    // Permission, SDK, quota, missing postal code and network failures all keep
    // the existing saved-address flow. Do not log coordinates or provider errors.
    return null;
  } finally {
    window.clearTimeout(deadline);
    parentSignal?.removeEventListener('abort', stop);
    if (!succeeded && !parentSignal?.aborted) {
      // Only our own failed loader may be replaced on retry. Never remove a
      // script owned by another map component or store coordinates/errors.
      onUnavailable?.(issue);
    }
  }
}

function pincodeFromResults(results: google.maps.GeocoderResult[]): CurrentLocationPincode | null {
  for (const result of results) {
    const component = (type: string) => result.address_components.find(c => c.types.includes(type));
    const pincode = component('postal_code')?.long_name.trim() ?? '';
    if (component('country')?.short_name !== 'IN' || !/^[1-9][0-9]{5}$/.test(pincode)) continue;
    return { pincode, cityName: component('locality')?.long_name || component('administrative_area_level_3')?.long_name || '',
      areaName: component('sublocality_level_1')?.long_name || component('sublocality')?.long_name || component('neighborhood')?.long_name || '' };
  }
  return null;
}

async function ensureGeocoder(signal: AbortSignal): Promise<boolean> {
  if (mapsClient()?.Geocoder) return true;
  // Reuse a loaded or in-flight Maps script from the existing map/address UI.
  // Do not copy its embedded keys or replace another component's script.
  let script = document.querySelector<HTMLScriptElement>('script[src^="https://maps.googleapis.com/maps/api/js"]');
  if (script?.dataset.farm2meLocationClient === 'true' && script.dataset.locationFailed === 'true') {
    script.remove();
    script = null;
  }
  if (!script) {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key || signal.aborted) return false;
    script = document.createElement('script');
    script.id = 'google-map-script';
    script.dataset.farm2meLocationClient = 'true';
    const query = new URLSearchParams({ key, v: 'weekly', libraries: 'core,marker,places' });
    script.src = 'https://maps.googleapis.com/maps/api/js?' + query.toString();
    script.async = true;
    document.head.appendChild(script);
  }
  const pendingScript = script;
  return new Promise<boolean>(resolve => {
    const finish = (ready: boolean) => {
      window.clearInterval(poll);
      signal.removeEventListener('abort', cancel);
      pendingScript.removeEventListener('error', failed);
      resolve(ready);
    };
    const cancel = () => finish(false);
    const failed = () => {
      if (pendingScript.dataset.farm2meLocationClient === 'true') pendingScript.dataset.locationFailed = 'true';
      finish(false);
    };
    const poll = window.setInterval(() => { if (mapsClient()?.Geocoder) finish(true); }, 100);
    signal.addEventListener('abort', cancel, { once: true });
    pendingScript.addEventListener('error', failed, { once: true });
    if (signal.aborted) finish(false);
    else if (mapsClient()?.Geocoder) finish(true);
  });
}
